import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useDeletedReports, useRestoreDeletedReport, useTenants } from '../api/admin'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, fmtDate, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

/** SuperAdmin: every report users deleted, across tenants, with a one-click restore. Tenants never see these. */
export default function DeletedReportsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isSuper = user?.roles.some((r) => r.role === 'SuperAdmin') ?? false
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  const page = Number(params.get('page') ?? 1)
  const search = params.get('search') ?? ''
  const tenantId = params.get('tenant') ?? ''
  const tenants = useTenants(isSuper)
  const rows = useDeletedReports(isSuper, { page, search, tenantId })
  const restore = useRestoreDeletedReport()
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  if (!isSuper) return <p className="error">{t('tenants.superOnly')}</p>

  const onRestore = (id: string) => {
    if (!confirm(t('reports.restoreConfirm'))) return
    setError(null)
    setInfo(null)
    restore.mutateAsync(id).then(() => setInfo(t('reports.restored'))).catch((e: unknown) => setError(errMsg(e)))
  }

  const data = rows.data
  const pageCount = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1

  return (
    <div className="stack">
      <div>
        <h2 style={{ margin: 0 }}>{t('reports.deletedTitle')}</h2>
        <p className="muted small" style={{ margin: '4px 0 0' }}>{t('reports.deletedHint')}</p>
      </div>
      <div className="card filters">
        <input placeholder={t('common.search')} value={search} onChange={(e) => set('search', e.target.value)} />
        <select value={tenantId} onChange={(e) => set('tenant', e.target.value)} aria-label={t('reports.tenant')}>
          <option value="">{t('reports.allTenants')}</option>
          {(tenants.data?.items ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </div>
      {info ? <p className="muted small" role="status">{info}</p> : null}
      {error ? <p className="error small" role="alert">{error}</p> : null}
      <div className="card">
        {rows.isLoading ? <p>{t('common.loading')}</p> : rows.isError ? <p className="error">{t('common.loadFailed')}</p> : !data?.items.length ? <Empty text={t('reports.deletedEmpty')} /> : (
          <div className="table-wrap"><table className="table">
            <thead><tr>
              <th>{t('reports.tenant')}</th><th>{t('reports.code')}</th><th>{t('reports.title')}</th><th>{t('reports.company')}</th><th>{t('reports.status')}</th>
              <th>{t('reports.deletedBy')}</th><th>{t('reports.deletedAt')}</th><th className="num">{t('reports.findings')}</th><th className="num">{t('reports.photos')}</th><th />
            </tr></thead>
            <tbody>{data.items.map((r) => (
              <tr key={r.id}>
                <td>{r.tenantName}</td>
                <td className="small"><b>{r.code ?? '—'}</b><div className="muted small">{r.templateName}</div></td>
                <td>{r.title ?? '—'}{r.auditStartDate ? <div className="muted small">{fmtDate(r.auditStartDate)}</div> : null}</td>
                <td>{r.companyName}</td>
                <td><Badge>{t(`status.${r.status}`)}</Badge></td>
                <td>{r.deletedByName ?? '—'}</td>
                <td className="small">{r.deletedAt ? fmtDateTime(r.deletedAt) : '—'}</td>
                <td className="num">{r.findingCount}</td>
                <td className="num">{r.photoCount}</td>
                <td><button className="secondary small-btn" onClick={() => onRestore(r.id)} disabled={restore.isPending}>{t('reports.restore')}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {pageCount > 1 ? (
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="ghost" disabled={page <= 1} onClick={() => set('page', String(page - 1))}>‹</button>
            <span className="small">{page} / {pageCount}</span>
            <button className="ghost" disabled={page >= pageCount} onClick={() => set('page', String(page + 1))}>›</button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
